import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, matchPath } from "react-router-dom";
import { API, api } from "@/lib/api";
import { toast } from "sonner";
import { Robot, PaperPlaneTilt, X, Sparkle, Paperclip, Question, ChatCircleText, ImageSquare, SidebarSimple, UploadSimple } from "@phosphor-icons/react";

const MAX_ATTACHMENTS = 1;
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "application/pdf"];

const money = (value) => `$${(Number(value) || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

function mapHistoryMessage(item) {
  return {
    role: item.role,
    content: item.content,
    attachments: item.image_names || [],
    why: item.supporting_data || null,
  };
}

function buildWhyRows(support) {
  if (!support) return [];
  if (support.scope === "portfolio") {
    return [
      { label: "Businesses tracked", value: String(support.business_count || 0) },
      { label: "Total cash", value: money(support.total_cash) },
      { label: "Overdue invoices", value: `${support.overdue_invoices?.count || 0} · ${money(support.overdue_invoices?.total)}` },
      { label: "Open anomalies", value: String(support.anomalies?.count || 0) },
      { label: "Urgent businesses", value: (support.urgent_businesses || []).map((item) => `${item.name} (${item.runway_label})`).join(", ") || "None yet" },
    ];
  }
  return [
    { label: "Revenue this month", value: money(support.current_month?.revenue) },
    { label: "Expenses this month", value: money(support.current_month?.expenses) },
    { label: "Net this month", value: money(support.current_month?.net) },
    { label: "Cash runway", value: support.cash_runway?.label || "Unknown" },
    { label: "Open invoices", value: `${support.open_invoices?.count || 0} · ${money(support.open_invoices?.total)}` },
    { label: "Overdue invoices", value: `${support.open_invoices?.overdue_count || 0} · ${money(support.open_invoices?.overdue_total)}` },
    { label: "Open bills", value: `${support.open_bills?.count || 0} · ${money(support.open_bills?.total)}` },
    { label: "Open anomalies", value: String(support.anomalies?.count || 0) },
    { label: "Top vendors", value: (support.top_vendors || []).map((item) => `${item.name} (${money(item.total)})`).join(", ") || "None yet" },
  ];
}

const SCREEN_SUGGESTIONS = {
  dashboard: [
    "What's the biggest thing I should fix first?",
    "Explain my cash position in plain English.",
    "Which business needs me most right now?",
  ],
  business: [
    "What changed in this business this month?",
    "Explain the biggest money risk you see here.",
    "What should I clean up next?",
  ],
  books: [
    "Explain this ledger in plain English.",
    "What should I review before I trust these imported transactions?",
    "How do I clean up this month without making a mess?",
  ],
  accounting: [
    "Explain this report like I'm smart but busy.",
    "What number looks off here?",
    "How do I reconcile this without making it worse?",
  ],
  taxes: [
    "How much should I set aside right now?",
    "Explain this tax number in plain English.",
    "What do I actually do next for quarterly taxes?",
  ],
  formation: [
    "Which entity type fits this kind of business?",
    "What formation step comes next?",
    "Explain the EIN step like I'm doing it today.",
  ],
  receipts: [
    "What does this receipt mean and how should I book it?",
    "Why didn't this receipt match automatically?",
    "Show me the fastest way to clean up unmatched receipts.",
  ],
  invoices: [
    "Which invoice needs my attention first?",
    "Explain my overdue invoices in plain English.",
    "What should I send to get paid faster?",
  ],
  diagnose: [
    "Walk me through this cleanup plan step by step.",
    "What broke in my books, exactly?",
    "What should I fix first and why?",
  ],
  guide: [
    "Explain this step without jargon.",
    "What mistake do people make here?",
    "What would Rowan do first?",
  ],
  settings: [
    "What does this billing status actually mean?",
    "Explain my plan in plain English.",
    "What can my team members access?",
  ],
};

function buildSessionId(scopeKey) {
  const key = `rowan-session:${scopeKey}`;
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const next = `${scopeKey}:${Math.random().toString(36).slice(2, 10)}`;
  sessionStorage.setItem(key, next);
  return next;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function screenMeta(pathname, search) {
  if (matchPath("/business/:id", pathname)) {
    const params = new URLSearchParams(search);
    const tab = params.get("tab") || "overview";
    const tabKey = ["books", "accounting", "taxes", "receipts", "invoices", "diagnose"].includes(tab) ? tab : (tab === "overview" ? "business" : "business");
    return {
      scopeKey: `business:${pathname.split("/").pop()}:${tab}`,
      screenKey: tabKey,
      label: tab === "overview" ? "Business workspace" : `${tab.replace(/_/g, " ")} tab`,
    };
  }
  if (pathname === "/dashboard") return { scopeKey: "dashboard", screenKey: "dashboard", label: "Dashboard" };
  if (pathname === "/formation") return { scopeKey: "formation", screenKey: "formation", label: "Formation" };
  if (pathname === "/settings") return { scopeKey: "settings", screenKey: "settings", label: "Settings" };
  if (matchPath("/guide/:phase", pathname)) return { scopeKey: pathname, screenKey: "guide", label: "Guide" };
  if (pathname === "/admin") return { scopeKey: "admin", screenKey: "dashboard", label: "Admin" };
  return { scopeKey: pathname || "app", screenKey: "dashboard", label: "Workspace" };
}

export function AiChat({ businessId = null, businessName = "", context = "", forceVisible = true }) {
  const location = useLocation();
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  const meta = useMemo(() => screenMeta(location.pathname, location.search), [location.pathname, location.search]);
  const sessionId = useMemo(() => buildSessionId(meta.scopeKey), [meta.scopeKey]);
  const suggestions = SCREEN_SUGGESTIONS[meta.screenKey] || SCREEN_SUGGESTIONS.dashboard;
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [queuedPrompt, setQueuedPrompt] = useState("");
  const [expandedWhy, setExpandedWhy] = useState({});
  const [retry, setRetry] = useState(null);

  const syncLatestHistory = async () => {
    const res = await api.get(`/chat/${sessionId}`);
    setMessages((res.data || []).slice(-10).map(mapHistoryMessage));
  };

  const screenContext = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return [
      `Current route: ${location.pathname}${location.search || ""}`,
      `Current screen: ${meta.label}`,
      params.get("tab") ? `Selected tab: ${params.get("tab")}` : "",
      params.get("txn") ? `Focused transaction id: ${params.get("txn")}` : "",
      businessName ? `Business name in UI: ${businessName}` : "",
      context || "",
    ].filter(Boolean).join("\n");
  }, [businessName, context, location.pathname, location.search, meta.label]);

  useEffect(() => {
    let ignore = false;
    if (!forceVisible) return undefined;
    setLoadingHistory(true);
    api.get(`/chat/${sessionId}`).then((res) => {
      if (ignore) return;
      const history = (res.data || []).slice(-10).map(mapHistoryMessage);
      setMessages(history);
      setExpandedWhy({});
    }).catch(() => {
      if (!ignore) setMessages([]);
    }).finally(() => {
      if (!ignore) setLoadingHistory(false);
    });
    return () => { ignore = true; };
  }, [sessionId, forceVisible]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    const handler = (e) => {
      setOpen(true);
      const text = e.detail?.text;
      const extraContext = e.detail?.context;
      const promptTitle = e.detail?.title;
      if (promptTitle) setQueuedPrompt(promptTitle);
      if (text) {
        setTimeout(() => {
          send(text, { extraContext });
        }, 250);
      }
    };
    window.addEventListener("bk-ask-ai", handler);
    return () => window.removeEventListener("bk-ask-ai", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming, sessionId, screenContext, attachments, businessId]);

  const onAttachFiles = async (files) => {
    const picked = Array.from(files || []);
    if (picked.length > MAX_ATTACHMENTS) {
      toast.info("Send one at a time and Rowan will walk through each one.");
    }
    const next = [];
    for (const file of picked.slice(0, MAX_ATTACHMENTS)) {
      if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
        toast.error(`Rowan reads JPG, PNG, and PDF files — ${file.name} is a different format. Take a screenshot of it and send that instead.`);
        continue;
      }
      if (file.size > MAX_ATTACHMENT_SIZE) {
        toast.error(`That file is over 10 MB — try a compressed version or take a screenshot of the relevant section.`);
        continue;
      }
      const dataUrl = await readFileAsDataUrl(file);
      next.push({
        name: file.name,
        preview: dataUrl,
        size: file.size,
        type: file.type,
      });
    }
    if (next.length) {
      setAttachments(next.slice(0, MAX_ATTACHMENTS));
      setQueuedPrompt("What is this and what do I do with it?");
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (name) => {
    setAttachments((current) => current.filter((item) => item.name !== name));
  };

  const send = async (text, opts = {}) => {
    const content = (text ?? input).trim();
    const outgoingAttachments = opts.attachmentsOverride || [...attachments];
    if ((!content && outgoingAttachments.length === 0) || streaming) return;
    setOpen(true);
    setRetry(null);
    const extraContext = opts.extraContext ? `\n${opts.extraContext}` : "";
    const uiContext = `${screenContext}${extraContext}`.trim();
    setInput("");
    setAttachments([]);
    setQueuedPrompt("");
    setMessages((m) => [
      ...m.slice(-9),
      { role: "user", content: content || "Can you explain what this image means?", attachments: outgoingAttachments.map((item) => item.name) },
      { role: "assistant", content: "" },
    ]);
    setStreaming(true);
    let failedRun = false;

    try {
      const token = localStorage.getItem("bk_token");
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          session_id: sessionId,
          message: content || "What is this and what do I do with it?",
          business_id: businessId,
          ui_context: uiContext,
          images: outgoingAttachments.map((item) => item.preview),
          image_names: outgoingAttachments.map((item) => item.name),
        }),
      });
      if (!res.ok || !res.body) throw new Error("stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let received = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        received += chunk;
        setMessages((m) => {
          const copy = [...m];
          const lastMsg = copy[copy.length - 1];
          if (lastMsg && lastMsg.role === "assistant") {
            copy[copy.length - 1] = { role: "assistant", content: (lastMsg.content || "") + chunk };
          }
          return copy.slice(-10);
        });
      }
      if (!received.trim()) throw new Error("empty response");
    } catch (e) {
      failedRun = true;
      setRetry({ text: content, extraContext: opts.extraContext || "", attachments: outgoingAttachments });
      setMessages((m) => {
        const copy = [...m];
        const lastMsg = copy[copy.length - 1];
        if (lastMsg && lastMsg.role === "assistant") {
          copy[copy.length - 1] = { role: "assistant", content: "", failed: true };
        }
        return copy.slice(-10);
      });
    } finally {
      setStreaming(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (!failedRun) syncLatestHistory().catch(() => {});
    }
  };

  if (!forceVisible) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="open-ai-chat"
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3.5 rounded-full text-white font-semibold shadow-lg bk-glow bk-pulse"
          style={{ background: "var(--bk-gradient-energy)" }}
        >
          <Robot size={22} weight="duotone" /> Ask Rowan
        </button>
      )}

      {open && (
        <>
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="ai-chat-backdrop"
            aria-label="Close Rowan panel"
            className="fixed inset-0 z-40 bg-[rgba(26,39,68,0.24)] backdrop-blur-[1px]"
          />
          <aside
            data-testid="ai-chat-side-panel"
            className="fixed inset-y-0 right-0 z-50 w-full sm:w-[500px] lg:w-[540px] max-w-full flex flex-col border-l shadow-[0_24px_60px_rgba(26,39,68,0.26)]"
            style={{ borderColor: "rgba(139,74,42,0.18)", background: "linear-gradient(180deg, #FCFAF6 0%, #F4ECDD 100%)" }}
          >
            <div className="px-5 py-5 border-b" style={{ borderColor: "rgba(139,74,42,0.14)", background: "rgba(255,255,255,0.74)" }}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3 min-w-0">
                  <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 shadow-sm" style={{ background: "var(--bk-accent)" }}>
                    <Robot size={22} weight="duotone" color="#fff" />
                  </span>
                  <div className="min-w-0">
                    <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em]" style={{ background: "rgba(240,230,211,0.92)", color: "var(--bk-accent)" }} data-testid="ai-chat-panel-badge">
                      <SidebarSimple size={12} weight="bold" /> Explain this screen
                    </div>
                    <p className="font-display font-bold text-xl mt-3 leading-tight" style={{ color: "var(--bk-text)" }}>Rowan side panel</p>
                    <p className="text-sm mt-1 truncate" style={{ color: "var(--bk-text-2)" }} data-testid="ai-chat-context-label">
                      {businessName ? `${meta.label} · ${businessName}` : `${meta.label} · context-aware help`}
                    </p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} data-testid="close-ai-chat" className="w-10 h-10 rounded-full border bg-white/80 flex items-center justify-center shrink-0">
                  <X size={18} weight="bold" color="var(--bk-text-2)" />
                </button>
              </div>

              <div className="mt-4 rounded-[22px] border p-4" style={{ borderColor: "rgba(139,74,42,0.12)", background: "rgba(255,255,255,0.72)" }} data-testid="ai-chat-screen-summary">
                <div className="flex items-start gap-2 text-sm" style={{ color: "var(--bk-text)" }}>
                  <ChatCircleText size={18} weight="duotone" color="var(--bk-accent)" className="mt-0.5 shrink-0" />
                  <div>
                    <p data-testid="ai-chat-rowan-intro">Good news: I know where you are in the app. Bad news: that means I can be useful.</p>
                    <p className="text-xs mt-2" style={{ color: "var(--bk-text-2)" }} data-testid="ai-chat-screen-path">
                      {location.pathname}{location.search || ""}
                    </p>
                    {queuedPrompt && (
                      <p className="text-xs mt-2 font-semibold" style={{ color: "var(--bk-accent)" }} data-testid="ai-chat-queued-prompt">
                        Suggested focus: {queuedPrompt}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto bk-scroll p-4 space-y-4" style={{ background: "transparent" }}>
            {loadingHistory && messages.length === 0 && (
              <p className="text-sm" style={{ color: "var(--bk-text-2)" }} data-testid="ai-chat-loading-history">Loading your Rowan history…</p>
            )}
            {messages.length === 0 && !loadingHistory && (
              <div className="pt-2 space-y-4">
                <div className="rounded-[24px] border p-4 bg-white/84" style={{ borderColor: "rgba(139,74,42,0.12)" }} data-testid="ai-chat-suggestions-card">
                  <div className="flex items-center gap-2 mb-3" style={{ color: "var(--bk-accent)" }}>
                    <Sparkle size={18} weight="fill" />
                    <span className="text-sm font-semibold">Ask Rowan about this screen</span>
                  </div>
                  <div className="space-y-2">
                    {suggestions.map((s) => (
                      <button key={s} onClick={() => send(s)} data-testid={`ai-suggestion-${s.slice(0, 16).replace(/\s+/g, "-").toLowerCase()}`} className="block w-full text-left text-sm px-4 py-3 rounded-md bg-white border hover:border-[#D96C4A] transition-colors" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text-2)" }}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rounded-[24px] border p-4 bg-white/84" style={{ borderColor: "rgba(139,74,42,0.12)" }} data-testid="ai-chat-upload-card">
                  <div className="flex items-center gap-2 mb-2" style={{ color: "var(--bk-text)" }}>
                    <ImageSquare size={18} weight="duotone" color="var(--bk-accent)" />
                    <p className="text-sm font-semibold">Show Rowan a screenshot</p>
                  </div>
                  <p className="text-xs" style={{ color: "var(--bk-text-2)" }}>Upload a screenshot, statement, or weird-looking number. Rowan will explain what it is and what to do next.</p>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="ai-chat-upload-card-button"
                    className="mt-3 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold bg-white"
                    style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}
                  >
                    <UploadSimple size={14} weight="bold" /> Upload JPG, PNG, or PDF
                  </button>
                </div>
              </div>
            )}

            {messages.map((m, i) => (
              <div key={`${m.role}-${i}`} data-testid={`chat-msg-${m.role}-${i}`} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div
                  className="max-w-[88%] text-sm leading-relaxed rounded-2xl px-4 py-3 whitespace-pre-wrap"
                  style={
                    m.role === "user"
                      ? { background: "#FFFFFF", border: "1px solid var(--bk-border)", color: "var(--bk-text)" }
                      : { background: "#FFFFFF", borderLeft: "3px solid var(--bk-accent)", color: "var(--bk-text)" }
                  }
                >
                  {m.attachments?.length > 0 && (
                    <p className="text-xs mb-2" style={{ color: "var(--bk-text-2)" }} data-testid={`chat-msg-attachments-${i}`}>
                      Attached: {m.attachments.join(", ")}
                    </p>
                  )}
                  {m.role === "assistant" && m.failed ? (
                    <div data-testid={`ai-chat-failed-${i}`}>
                      <p>Rowan hit a snag answering that one. Your message wasn't lost — give it another shot.</p>
                      <button
                        type="button"
                        onClick={() => retry && send(retry.text, { extraContext: retry.extraContext, attachmentsOverride: retry.attachments })}
                        disabled={streaming || !retry}
                        data-testid="ai-chat-retry-button"
                        className="mt-3 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold bg-white disabled:opacity-50"
                        style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}
                      >
                        Try again
                      </button>
                    </div>
                  ) : (
                    m.content || (streaming && i === messages.length - 1 ? "…" : "")
                  )}
                  {m.role === "assistant" && m.why && (
                    <div className="mt-3 pt-3 border-t" style={{ borderColor: "rgba(139,74,42,0.12)" }}>
                      <button
                        type="button"
                        onClick={() => setExpandedWhy((current) => ({ ...current, [i]: !current[i] }))}
                        data-testid={`ai-chat-why-toggle-${i}`}
                        className="inline-flex items-center gap-2 text-xs font-semibold"
                        style={{ color: "var(--bk-accent)" }}
                      >
                        <Question size={13} weight="bold" /> Why I said this
                      </button>
                      {expandedWhy[i] && (
                        <div className="mt-3 rounded-[18px] border p-3 space-y-2 bg-[#FFFCF6]" style={{ borderColor: "rgba(139,74,42,0.12)" }} data-testid={`ai-chat-why-panel-${i}`}>
                          <p className="text-[11px] uppercase tracking-[0.16em] font-semibold" style={{ color: "var(--bk-text-2)" }}>Live data Rowan used</p>
                          {buildWhyRows(m.why).map((row) => (
                            <div key={`${i}-${row.label}`} className="flex items-start justify-between gap-3 text-xs" data-testid={`ai-chat-why-row-${i}-${row.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                              <span style={{ color: "var(--bk-text-2)" }}>{row.label}</span>
                              <span className="font-medium text-right" style={{ color: "var(--bk-text)" }}>{row.value}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            </div>

            {attachments.length > 0 && (
              <div className="px-4 pt-4 flex flex-wrap gap-3 border-t" style={{ borderColor: "rgba(139,74,42,0.14)", background: "rgba(255,255,255,0.74)" }}>
              {attachments.map((item) => (
                <div key={item.name} className="inline-flex items-center gap-3 rounded-[18px] px-2.5 py-2 text-xs border bg-white" style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }} data-testid={`ai-chat-attachment-${item.name}`}>
                  {item.type === "application/pdf" ? (
                    <span className="w-10 h-10 rounded-xl border flex items-center justify-center bg-white" style={{ borderColor: "rgba(139,74,42,0.12)", color: "#B23B1F" }} data-testid={`ai-chat-attachment-preview-${item.name}`}>
                      <span className="text-[9px] font-bold">PDF</span>
                    </span>
                  ) : (
                    <img src={item.preview} alt={item.name} className="w-10 h-10 rounded-xl object-cover border" style={{ borderColor: "rgba(139,74,42,0.12)" }} data-testid={`ai-chat-attachment-preview-${item.name}`} />
                  )}
                  <span className="max-w-[180px] truncate">{item.name}</span>
                  <button type="button" onClick={() => removeAttachment(item.name)} data-testid={`ai-chat-remove-attachment-${item.name}`} className="w-7 h-7 rounded-full border flex items-center justify-center">
                    <X size={12} weight="bold" />
                  </button>
                </div>
              ))}
              </div>
            )}

            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="p-4 border-t flex items-end gap-2"
              style={{ borderColor: "rgba(139,74,42,0.14)", background: "rgba(255,255,255,0.82)" }}
            >
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="ai-chat-upload-button"
                className="w-11 h-11 rounded-2xl flex items-center justify-center border bg-white shrink-0"
                style={{ borderColor: "var(--bk-border)", color: "var(--bk-accent)" }}
              >
                <Paperclip size={18} weight="bold" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,application/pdf"
                multiple
                onChange={(e) => onAttachFiles(e.target.files)}
                className="hidden"
                data-testid="ai-chat-upload-input"
              />
              <div className="flex-1 space-y-2">
                <input
                  data-testid="ai-chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={attachments.length ? "Ask Rowan what this document means…" : "Ask Rowan anything about this screen…"}
                  className="w-full px-4 py-3 rounded-2xl border text-sm outline-none focus:ring-2 bg-white"
                  style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
                />
                <p className="text-[11px] px-1" style={{ color: "var(--bk-text-2)" }} data-testid="ai-chat-session-note">
                  Rowan keeps the last 10 messages for this screen in your current session. Files supported here: JPG, PNG, PDF (one at a time, up to 10MB).
                </p>
              </div>
              <button type="submit" disabled={streaming || (!input.trim() && attachments.length === 0)} data-testid="ai-chat-send" className="bk-btn-primary w-11 h-11 rounded-2xl flex items-center justify-center disabled:opacity-50 shrink-0">
                <PaperPlaneTilt size={20} weight="fill" />
              </button>
            </form>
          </aside>
        </>
      )}

      <button
        type="button"
        onClick={() => send(`Explain what I'm looking at on the ${meta.label} screen.`)}
        data-testid="ai-chat-explain-screen-button"
        className={`fixed bottom-24 right-6 z-40 hidden xl:flex items-center gap-2 px-4 py-2 rounded-full border bg-white text-sm font-semibold shadow-sm transition-opacity ${open || matchPath("/business/:id", location.pathname) ? "pointer-events-none opacity-0" : "opacity-100"}`}
        style={{ borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
      >
        <Question size={16} weight="bold" color="var(--bk-accent)" /> Explain this screen
      </button>
    </>
  );
}
